import { useProfile } from '@/store';
import { useFeatureFlags } from '@/store/featureFlags';

import { redirectWithLoopProtection } from './index';

export const requireAdminRedirect = (redirectRouteName = 'home') => {
  return async (to, from, next) => {
    const profileStore = useProfile();

    if (!profileStore.profile?.is_org_admin) {
      return redirectWithLoopProtection(to, next, redirectRouteName, 'requireAdminRedirect');
    }

    next();
  };
};

export enum FeatureAccess {
  BaseDisputes = 'base-disputes',
  CashApp = 'cash-app',
  Deductions = 'deductions',
  Disputes = 'disputes',
  Playbook = 'playbook',
}

const hasFeatureAccess = async (feature: FeatureAccess) => {
  const profileStore = useProfile();
  const profile = profileStore?.profile;

  switch (feature) {
    case FeatureAccess.Playbook: {
      const featureFlagsStore = useFeatureFlags();
      await featureFlagsStore.loadFeatureFlags();
      const playbooksFlag = featureFlagsStore.flags.playbooks;
      return playbooksFlag === 'use-playbooks' || playbooksFlag === 'use-both';
    }
    case FeatureAccess.BaseDisputes: {
      await useFeatureFlags().loadFeatureFlags();
      return Boolean(profileStore?.isBaseDisputesEnabled);
    }
    case FeatureAccess.CashApp:
      return Boolean(profile?.isCashAppEnabled);
    case FeatureAccess.Deductions:
      return Boolean(profile?.isDeductionsEnabled);
    case FeatureAccess.Disputes:
      return Boolean(profile?.isDisputesEnabled);
  }
};

export const requiresFeatureAccess = (feature: FeatureAccess, name: string = 'home') => {
  return async (to, from, next) => {
    const hasAccess = await hasFeatureAccess(feature);

    return hasAccess
      ? next()
      : redirectWithLoopProtection(to, next, name, 'requiresFeatureAccess', to.query);
  };
};

/**
 * Guard tied to the `mailbox-settings` flag. Enters the route when the flag's
 * enabled state matches `expectedEnabled`; otherwise redirects to `redirectTo`.
 * Used to keep the new mailbox page and the legacy Connected Email page in sync:
 * the new page redirects to legacy when the flag is off, and legacy redirects to
 * the new page when the flag is on.
 */
export const requireMailboxSettingsFlag = (expectedEnabled: boolean, redirectTo: string) => {
  return async (to, from, next) => {
    const featureFlagsStore = useFeatureFlags();
    await featureFlagsStore.loadFeatureFlags();
    const enabled = Boolean(featureFlagsStore.flags['mailbox-settings']);

    return enabled === expectedEnabled
      ? next()
      : redirectWithLoopProtection(to, next, redirectTo, 'requireMailboxSettingsFlag', to.query);
  };
};

/**
 * Base-disputes orgs (base flag on, Disputes Pro off) don't get the Needs Review
 * queue — their disputes playbook is just Reason Codes. Send them straight there so
 * the hidden Needs Review nav item is never their landing page.
 */
export const redirectBaseDisputesToReasonCodes = () => {
  return async (to, from, next) => {
    await useFeatureFlags().loadFeatureFlags();
    const profileStore = useProfile();

    return profileStore.isDisputesBaseOnly
      ? next({ name: 'playbook.disputes.codes', query: to.query })
      : next();
  };
};
